import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { norm } from "../domain/eventoUtils";

export function ArmaSearchSelect({
    armas = [],
    valueId,
    onChange,
    placeholder = "Buscar arma por tipo, calibre, guía, serie, titular, país…",
}) {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");
    const [dropdownDir, setDropdownDir] = useState("down");
    const [dropdownRect, setDropdownRect] = useState(null);
    const wrapRef = useRef(null);

    const selected = useMemo(
        () => armas.find((a) => Number(a._id) === Number(valueId)) || null,
        [armas, valueId]
    );

    const filtered = useMemo(() => {
        const query = norm(q);
        if (!query) return armas.slice(0, 50);
        return armas.filter((a) => a._search.includes(query)).slice(0, 50);
    }, [armas, q]);

    useEffect(() => {
        const onDoc = (e) => {
            if (!wrapRef.current) return;
            if (wrapRef.current.contains(e.target)) return;
            if (e.target.closest?.('[data-arma-search-portal="true"]')) return;
            setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    // Posiciona el desplegable en coordenadas de viewport (position: fixed) para que
    // no quede recortado por contenedores con overflow (ej. la tabla con scroll horizontal).
    const updatePosition = () => {
        const el = wrapRef.current;
        if (!el) return;

        const rect = el.getBoundingClientRect();
        const viewportH = window.innerHeight || document.documentElement.clientHeight;

        const spaceBelow = viewportH - rect.bottom;
        const spaceAbove = rect.top;
        const dir = spaceBelow < 276 && spaceAbove > spaceBelow ? "up" : "down";

        setDropdownDir(dir);
        setDropdownRect({
            left: rect.left,
            width: rect.width,
            top: dir === "down" ? rect.bottom + 4 : null,
            bottom: dir === "up" ? viewportH - rect.top + 4 : null,
        });
    };

    useLayoutEffect(() => {
        if (!open) return;
        updatePosition();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, q]);

    useEffect(() => {
        if (!open) return;
        const onScrollOrResize = () => updatePosition();
        window.addEventListener("scroll", onScrollOrResize, true);
        window.addEventListener("resize", onScrollOrResize);
        return () => {
            window.removeEventListener("scroll", onScrollOrResize, true);
            window.removeEventListener("resize", onScrollOrResize);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    return (
        <div ref={wrapRef} style={{ position: "relative" }}>
            <input
                type="text"
                value={open ? q : selected?._label || ""}
                placeholder={placeholder}
                onFocus={() => {
                    setOpen(true);
                    setQ("");
                }}
                onChange={(e) => {
                    setOpen(true);
                    setQ(e.target.value);
                }}
            />

            {open && dropdownRect &&
                createPortal(
                    <div
                        data-arma-search-portal="true"
                        style={{
                            position: "fixed",
                            zIndex: 99999,
                            left: dropdownRect.left,
                            width: dropdownRect.width,
                            top: dropdownDir === "down" ? dropdownRect.top : "auto",
                            bottom: dropdownDir === "up" ? dropdownRect.bottom : "auto",
                            background: "white",
                            border: "1px solid rgba(0,0,0,0.15)",
                            borderRadius: 8,
                            maxHeight: 260,
                            overflow: "auto",
                            boxShadow: "0 12px 28px rgba(0,0,0,0.18)",
                        }}
                    >
                        {filtered.length === 0 ? (
                            <div style={{ padding: 10, opacity: 0.7 }}>Sin resultados.</div>
                        ) : (
                            filtered.map((a) => (
                                <button
                                    key={a._id}
                                    type="button"
                                    onClick={() => {
                                        onChange?.(Number(a._id));
                                        setOpen(false);
                                        setQ("");
                                    }}
                                    style={{
                                        display: "block",
                                        width: "100%",
                                        textAlign: "left",
                                        padding: 10,
                                        border: "none",
                                        background: "transparent",
                                        cursor: "pointer",
                                    }}
                                    title={a._label}
                                >
                                    {a._label}
                                </button>
                            ))
                        )}
                    </div>,
                    document.body
                )}
        </div>
    );
}
